"use client";

import { useEffect, useState } from "react";
import { ChevronDown, Plus, BookOpen, Check } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Notebook } from "../../client";
import { CreateNotebookModal } from "./create-notebook-modal";
import { CanvasCourse } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { FinalAssignment } from "../../types";
import { CourseAssignmentInline } from "./study-sets/course-assignment-inline";

interface NotebookSwitcherProps {
  courses: CanvasCourse[];
  notebooks: Notebook[];
  selectedNotebook?: Notebook;
  onNotebookChange: (notebook: Notebook) => void;
  openLanding: () => void;
  enterCreateFlow: () => void;
}

export function NotebookSwitcher({
  notebooks,
  selectedNotebook,
  onNotebookChange,
  courses,
  openLanding,
  enterCreateFlow,
}: NotebookSwitcherProps) {
  const getCourseForNotebook = (notebook?: Notebook) =>
    notebook
      ? courses.find((c) => c.id.toString() === notebook.course_id)
      : null;
  const selectedNotebookCourse = getCourseForNotebook(selectedNotebook);
  const selectedNotebookAssignment = selectedNotebook?.assignment_id
    ? (selectedNotebook.metadata.assignment as FinalAssignment).name
    : null;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            {/* Notebook selector */}
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                className="
    relative overflow-hidden
    w-full h-auto px-2 py-2.5
    rounded-md
    transition cursor-pointer
  "
                style={
                  {
                    "--accent":
                      selectedNotebookCourse?.color ||
                      "oklch(70.5% 0.213 47.604)",
                  } as React.CSSProperties
                }
              >
                {/* overlay */}
                <div
                  className="
      absolute inset-0
      bg-[var(--accent)]
      opacity-15
      pointer-events-none
      hover:opacity-20
      transition-opacity
    "
                  style={{
                    backgroundImage: selectedNotebookCourse?.img
                      ? `url(${selectedNotebookCourse?.img})`
                      : "none",
                  }}
                />
                {/* Icon */}
                {/* <div className="flex size-7 items-center justify-center rounded-md text-theme-0 shrink-0">
                  <BookOpen className="size-4" />
                </div> */}

                {/* Notebook + course */}
                <div className="flex-1 min-w-0 pl-2 group-data-[collapsible=icon]:hidden">
                  <div className="truncate font-bold leading-tight text-theme-0">
                    {selectedNotebook?.name || "Untitled Study Set"}
                  </div>

                  {selectedNotebookCourse ? (
                    <CourseAssignmentInline
                      className="mt-1"
                      color
                      course={selectedNotebookCourse}
                      assignmentName={selectedNotebookAssignment}
                    />
                  ) : (
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-1 truncate">
                      <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-[var(--accent)]" />
                      <span className="truncate">No course selected</span>
                    </div>
                  )}
                </div>

                <ChevronDown className="size-4 text-theme-2 shrink-0" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-64 rounded-lg bg-theme-0"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="text-theme-1 text-xs p-0">
                  Study Sets
                </DropdownMenuLabel>

                <button
                  type="button"
                  className="text-xs text-theme-1 hover:text-theme-0 transition"
                  onClick={openLanding}
                >
                  View all
                </button>
              </div>

              <DropdownMenuSeparator className="border-t-1 border-theme" />

              {/* Scrollable list */}
              <div className="max-h-96 overflow-y-auto">
                {notebooks.map((notebook, index) => {
                  const isSelected = selectedNotebook
                    ? notebook.id === selectedNotebook.id
                    : false;

                  return (
                    <DropdownMenuItem
                      key={notebook.id}
                      onClick={() => onNotebookChange(notebook)}
                      className={cn(
                        "gap-2 p-3 cursor-pointer",
                        "hover:bg-theme-1",
                        isSelected && "bg-theme-1 font-medium"
                      )}
                    >
                      {/* Icon */}
                      {/* <div className="flex size-6 items-center justify-center rounded-sm border shrink-0">
                        <BookOpen className="size-4 shrink-0" />
                      </div> */}

                      {/* Notebook + course */}
                      <div className="flex-1 min-w-0">
                        <div className="truncate leading-tight">
                          {notebook.name}
                        </div>

                        {(() => {
                          const course = getCourseForNotebook(notebook);
                          if (!course)
                            return (
                              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-1 truncate">
                                <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-theme-2" />
                                <span className="truncate">
                                  No course selected
                                </span>
                              </div>
                            );

                          return (
                            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-1 truncate">
                              <span
                                className="h-1.5 w-1.5 rounded-full shrink-0"
                                style={{
                                  backgroundColor: course.color || "#6366f1",
                                }}
                              />
                              <span className="truncate">
                                {(course as any).default}
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Selected / shortcut */}
                      {isSelected ? (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <DropdownMenuShortcut>
                          ⌘{index + 1}
                        </DropdownMenuShortcut>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </div>

              <DropdownMenuSeparator className="border-t-1 border-theme mt-0" />

              <DropdownMenuItem
                className="gap-2 p-2 hover:scale-90 transition cursor-pointer"
                onClick={enterCreateFlow}
              >
                <div className="bg-background flex size-6 items-center justify-center rounded-md border border-theme">
                  <Plus className="size-4" />
                </div>
                <div className="text-theme-1 font-medium">Create study set</div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
